import "./Console.scss";
import React from "react";
import ReactDOM from "react-dom";
import {
  CollapseSvg,
  PinSvg,
  DownloadIcon,
  UploadIcon,
  ThreeDotIcon,
  CheckboxCheckedSvg,
  CheckboxUncheckedSvg,
  DraggableHandle,
  CloseIcon,
  TcTmCombinedIcon,
  TcTmCombinedGreenIcon,
  TcIconGreen,
  TmIconGreen,
} from "../Svgs";
import { BeaconSvg, HealthMetricSvg, InvalidTmSvg } from "./ConsoleSvg";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import TTCFormSection from "./TTCFormSection";
import TaskForm from "./TaskForm";
import ConsoleOutputSection from "./ConsoleOutputSection";
import FormSubmit from "./FormSubmit";

import TestFormSection from "./components/Test/Test";

import Modal from "../Modal";
import useModals from "../../../../hooks/useModals";
import useConsole from "../../../../hooks/useConsole";
import useUrlParams from "../../../../hooks/useUrlParams";

import RawOutputSection from "./RawOutputSection";
import QueuePanel from "./QueuePanel";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import CustomSelect from "../../../../custom_components/CustomSelect";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Form } from "react-bootstrap";

import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { arrayMove } from "react-sortable-hoc";
import { get_formated_time } from "../../../../utils";

import useOrbit from "../../../../hooks/useOrbit";
import useTrueTwin from "../../../../hooks/useTrueTwin";
import { showToastMessage, uuidv4 } from "../../../../../../utils/commonFunctions";
import { SESSION_KEY_EXECUTE_ONBOARD, SESSION_KEY_FAILURE_HANDLING } from "../../../../../../utils/constants";
import { ConsoleLockIcon } from "../../hud/contact/Svgs";

import CustomFilter from "../../../../../CommonComponents/custom_filter/CustomFilter";
import { getBands, getFilterOption, isValidSessionId } from "./Utils";
import {
  AllFilterType,
  FilterListType,
} from "../../../../../CommonComponents/custom_filter/types";
import { ALL_BANDS, EPOCH_MAX_YEAR, DEFAULT_TABLE_WIDTH } from "./constants/consoleConstants";
import type { Option, SelectedFileWithData } from "./types/consoleTypes";
import type { Dayjs } from "dayjs";
import useSecurityConfig from "../../../../hooks/useSecurityConfig";
import useFilters from "../../../../hooks/useFilters";
import FilterGroupsHeader from "./FilterGroupsHeader";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../../../../simulate/constants";
import useScenario from "../../../../hooks/useScenario";
import useLoader from "../../../../hooks/useLoader";
import useSocket from "../../../../hooks/useSocket";
import { isOperateMode, isSummaryMode } from "../../../../hooks/utils";
import useValidationExecution from "../../../../../validate/hooks/useValidationExecution";

dayjs.extend(utc);

/** MUI DateTimePicker theme overrides for dark background */
const DATE_PICKER_SX = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "rgba(240, 240, 240, 0.30) !important" },
    "&:hover fieldset": { borderColor: "rgba(240, 240, 240, 0.30) !important" },
    "&.Mui-focused fieldset": { borderColor: "rgba(240, 240, 240, 0.30) !important" },
    width: "96% !important",
  },
  "& .MuiInputBase-root": { color: "white", backgroundColor: "rgba(10, 10, 10, 0.65)", fontSize: "15px" },
  "& .MuiButtonBase-root": { color: "black", padding: "0px" },
  "& .MuiDayPicker-day": { color: "black" },
  "& .Mui-selected": { backgroundColor: "#000", color: "white" },
};

/** Reads a file via FileReader and creates a FormData blob for upload */
const processFileForUpload = (
  file: File,
  onSuccess: (data: { file_data: FormData; name: string }) => void
) => {
  const reader = new FileReader();
  reader.onload = (e: any) => {
    const blob = new Blob([e.target.result], { type: "multipart/form-data" });
    const file_data = new FormData();
    file_data.append("file", blob, file.name);
    onSuccess({ file_data, name: file.name });
  };
  reader.readAsText(file);
};

/** Drag handle for sortable column rows */
const DragHandle = SortableHandle(() => (
  <div style={{ cursor: "grab" }}>
    <DraggableHandle />
  </div>
));

const VALIDATE_HIDDEN_COLUMN_LABELS = new Set(["Associated Task", "GS ID", "Type"]);
const DEFAULT_HIDDEN_COLUMN_LABELS = new Set(["Time", "Info"]);

const getDefaultColumnSelection = (label: string, isValidateMode: boolean) => {
  if (isValidateMode) {
    if (DEFAULT_HIDDEN_COLUMN_LABELS.has(label)) {
      return false;
    }
    return !VALIDATE_HIDDEN_COLUMN_LABELS.has(label);
  }
  return label !== "Results";
};

/** Sortable row item for column visibility toggle in three-dot menu */
const SortableRow = React.memo(
  SortableElement(({ rowData, row_index, setConsoleTableColumns, setIsTableWidth, isTableWidth }) => {
    const toggleSelected = () => {
      setConsoleTableColumns(prev => {
        const next = [...prev];
        const current = next[row_index];
        const nextSelected = !current.isSelected;
        next[row_index] = { ...current, isSelected: nextSelected };
        setIsTableWidth(prevWidth =>
          nextSelected
            ? prevWidth + Number(current.width)
            : prevWidth - Number(current.width)
        );
        return next;
      });
    };

    return (
      <div className="three_dot_console_body__item user-select-none">
        <DragHandle />
        <button onClick={toggleSelected}>
          {rowData.isSelected ? <CheckboxCheckedSvg /> : <CheckboxUncheckedSvg />}
        </button>
        <div className="user-select-none" onClick={toggleSelected}>
          {rowData.label}
        </div>
      </div>
    );
  })
);

/** Three-dot popup menu for column visibility and reordering */
const ThreeDotMenuPopUp = SortableContainer(({ threeDotMenuPopUpRef, isSelectAllFiltersActive, setIsTableWidth, consoleTableColumns, setConsoleTableColumns, operateMode, isTableWidth, dashboard, isValidateMode }) => (
  <>
    <div
      className="console__container__three_dot_menu_pop_up_container"
      ref={threeDotMenuPopUpRef}
      onClick={(e) => e.stopPropagation()}
      onDragStart={(e) => e.stopPropagation()}
      onMouseDown={(e) => { e.stopPropagation(); }}
      {...{ onSelectStart: (e: React.SyntheticEvent) => e.stopPropagation() } as any}
    >
      <div className="three_dot_console_header">
        <div>Fields</div>
        <button
          className={isSelectAllFiltersActive ? "active" : "inactive"}
          onClick={() => {
            setConsoleTableColumns((prev) => {
              const next = prev.map((item) => ({
                ...item,
                isSelected: getDefaultColumnSelection(item.label, isValidateMode),
              }));
              setIsTableWidth(
                next.reduce((sum, item) => (item.isSelected ? sum + Number(item.width) : sum), 0)
              );
              return next;
            });
          }}
        >
          Select All
        </button>
      </div>
      <div className="three_dot_console_body">
        {consoleTableColumns?.map((rowData, index) => {
          if (index === 0) return null;
          // In validate summary, show the same selectable fields as a running session.
          if (dashboard !== "operate" && !isValidateMode && rowData.dashboard === 'operate') return null;
          // Hide Results column unless in validate mode
          if (rowData.label === "Results" && !isValidateMode) return null;
          const AnyRow = SortableRow as any;
          return (
            <AnyRow
              key={`row-${index}`}
              index={index}
              rowData={rowData}
              row_index={index}
              setConsoleTableColumns={setConsoleTableColumns}
              setIsTableWidth={setIsTableWidth}
              isTableWidth={isTableWidth}
            />
          );
        })}
      </div>
    </div>
  </>
));

// ─── Communication mode type ──────────────────────────────────────────────────

type CommType = "TT&C" | "TASK" | "TEST" | "TEST PLAN";

/** Main console component with left panel (forms) and right panel (messages) */
const Console = () => {
  const { scenario } = useScenario()
  const { dashboard, mission_id, scenario_id, sat_id } = useUrlParams();
  const { loading: commandCentreFreeze } = useLoader();
  const { resetSecurityConfigFormData } = useSecurityConfig()
  const { loading, error, commandDetails, getCommandDetails, postData, busComponentOptions, fetchBusCommands, busCommandOptions, allPayloadComponents, uploadAPI, exportMessages } = useConsole();
  const { patchFilterGroup, consoleFilterGroups, consoleFilterOptions, setReferenceIdConsole, referenceIdConsole, getFilterOptions } = useFilters()
  const operateMode = isOperateMode(dashboard, scenario);
  const summaryMode = isSummaryMode(dashboard, scenario);
  const { orbitData } = useOrbit();
  const { truetwin_id } = useTrueTwin();
  const { socket } = useSocket();
  const { open_modal, close_modal, pinned_modal, get_modal_details, close_all_unpinned_modals } = useModals();

  const {
    activeTest,
    executedTests,
    isRunning: isValidationRunning,
    isAborting: isValidationAborting,
    abortActiveExecution,
    handleWebSocketMessage,
  } = useValidationExecution({
    missionId: mission_id,
    truetwinId: truetwin_id,
    scenarioId: scenario_id,
    socket,
  });

  /** Scenario deploymentType === VALIDATE (used for test tab + results column) */
  const isValidateMode = useMemo(
    () => scenario?.scenarioDeploymentType?.toUpperCase() === SCENARIO_DEPLOYMENT_TYPES.VALIDATE,
    [scenario?.scenarioDeploymentType]
  );

  const [resizing, setResizing] = useState(false);
  const [isLockHovered, setIsLockHovered] = useState(false);
  const [show_rawbytes, set_show_rawbytes] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [openPanel, setOpenPanel] = useState({ left: true, right: true });
  const [communicationType, setCommunicationType] = useState<CommType>("TT&C");
  const [commandType, setCommandType] = useState<Option | null>({ value: "Bus", label: "Bus Command" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SelectedFileWithData | null>(null);
  const [selectedDropFile, setSelectedDropFile] = useState<SelectedFileWithData | null>(null);
  const [band, setBand] = useState<null | { label: string; value: string }>(null);
  const [selectedTime, setSelectedTime] = useState<Dayjs>(dayjs.utc());
  const [currentTime, setCurrentTime] = useState<number>(Math.floor(dayjs.utc().valueOf() / 1000));
  const [groundStationId, setGroundStationId] = useState("");
  const [isConsoleMaximized, setIsConsoleMaximized] = useState(false);
  const [isTableWidth, setIsTableWidth] = useState(DEFAULT_TABLE_WIDTH);
  const [formData, setFormData] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formContainerHeight, setFormContainerHeight] = useState<number>(0);
  const [isQueuePanelOpen, setIsQueuePanelOpen] = useState(false);

  const handleTestFormChange = useCallback((values: Record<string, string>, isValid: boolean) => {
    setIsFormValid(isValid);
    setFormData(values as any);
  }, []);

  const modal = get_modal_details("console") as any;
  const multiplier = orbitData?.documentMetadata?.clock?.multiplier ?? 1;
  const scenario_completed = useMemo(() => scenario?.status === "SimulationCompleted", [scenario?.status]);

  useEffect(() => {
    setReferenceIdConsole(uuidv4()) // Reset console reference ID on open to allow toast notifications for new filter group WebSocket messages
    return () => {
      clearTestSessionStorage();
      sessionStorage.removeItem("sessionId");
    };
  }, []);

  // Opens the console modal and prevents event bubbling
  const handleOpenConsole = (event) => {
    event.stopPropagation();
    open_modal("console");
  }

  // Removes task-related keys from session storage on mode switch
  const clearTaskSessionStorage = () => {
    sessionStorage.removeItem("selected_task");
    sessionStorage.removeItem("selected_schedule");
  };
  const clearTestSessionStorage = () => {
    [
      "testTestCase",
      "testCaseId",
      "testFormValues",
      "testPlanPlan",
      "testPlanCaseId",
      "testPlanFormValues",
      "testPlanId",
      SESSION_KEY_EXECUTE_ONBOARD,
      SESSION_KEY_FAILURE_HANDLING,
    ].forEach((key) => sessionStorage.removeItem(key));
    window.dispatchEvent(new Event("storageUpdate"));
  };
  const handleOpenLeftPanel = (event) => {
    event.stopPropagation();
    if (summaryMode) return;
    open_panel("left");
  };
  const handlePinFunctionality = (event) => { event.stopPropagation(); pinned_modal("console"); };
  const handleCloseConsole = (event) => {
    event.stopPropagation();
    close_modal("console");
    set_show_rawbytes(false);
    setCommandType({ value: "Bus", label: "Bus Command" })
    sessionStorage.removeItem('taskType')
    sessionStorage.removeItem('sessionId')
    clearTestSessionStorage()
    setIsFrozen(false)
    cleanupFilterGroupsApi()
  }

  const cleanupFilterGroupsApi = () => {
    try {
      if (!patchFilterGroup) return;
      // Prefer the snapshot captured while mounted. If not available, fall back
      // to reading the live consoleFilterGroups (defensive).
      const groupsArrayFromSnapshot = (filterGroupsSnapshotRef.current && Array.isArray(filterGroupsSnapshotRef.current) && filterGroupsSnapshotRef.current.length) ? filterGroupsSnapshotRef.current : null;
      let groupsArray = groupsArrayFromSnapshot;
      if (!groupsArray) {
        // consoleFilterGroups might be a Map or similar iterable. Safely try to read values.
        groupsArray = Array.from((consoleFilterGroups && consoleFilterGroups.values && typeof consoleFilterGroups.values === 'function') ? consoleFilterGroups.values() : []);
      }
      if (!groupsArray || groupsArray.length === 0) return;
      // Use the snapshot requestId if available (stable across unmount/navigation)
      const requestIdToUse = filterGroupsReferenceIdRef.current ?? undefined;
      patchFilterGroup(false, groupsArray, () => { }, requestIdToUse);
    } catch (err) {
      // Don't throw during unmount/navigation - just log a warning for diagnostics.
      // This prevents failures when external state is already torn down.
      // eslint-disable-next-line no-console
      console.warn('[Console] cleanupFilterGroupsApi skipped due to error:', err);
    }
  }

  // Snapshot of consoleFilterGroups to use during unmount/cleanup. Keep as ref
  // so it's stable even if the Map/object is cleared before unmount.
  const filterGroupsSnapshotRef = React.useRef<any[] | null>(null);
  // Snapshot request id to use for patch calls during unmount/navigation.
  const filterGroupsReferenceIdRef = React.useRef<string | null>(null);

  // Keep the snapshot in sync whenever consoleFilterGroups changes while mounted.
  useEffect(() => {
    try {
      if (consoleFilterGroups && consoleFilterGroups.values && typeof consoleFilterGroups.values === 'function') {
        const arr = Array.from(consoleFilterGroups.values());
        filterGroupsSnapshotRef.current = arr;
        // capture the current request id from context so we can use it during cleanup
        filterGroupsReferenceIdRef.current = referenceIdConsole ?? null;
      } else {
        filterGroupsSnapshotRef.current = null;
        filterGroupsReferenceIdRef.current = null;
      }
    } catch (err) {
      filterGroupsSnapshotRef.current = null;
      filterGroupsReferenceIdRef.current = null;
    }
  }, [consoleFilterGroups]);

  const handleCommunicationBtnSelection = (type: CommType) => {
    if (type !== communicationType) {
      // Every communication mode owns a different selection contract.
      // Reset validity immediately so Execute cannot use a stale test/plan.
      setIsFormValid(false);
      setFormData(null);
      clearTestSessionStorage();
    }
    setCommunicationType(type);
    resetSecurityConfigFormData()
    if (type === "TASK") {
      if (!sessionStorage.getItem("taskType")) {
        sessionStorage.setItem("taskType", "file");
        clearTaskSessionStorage();
      }
    } else {
      sessionStorage.removeItem("taskType");
      clearTaskSessionStorage();
    }
  };

  const handleFormSubmit = (isValid, data) => {
    setIsFormValid(isValid);
    setFormData(data);
  };

  const openModal = () => { setIsModalOpen(true); setBand(null); setGroundStationId(""); };
  const closeModal = () => { setIsModalOpen(false); setSelectedFile(null); setSelectedDropFile(null); };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    processFileForUpload(file, (data) => { setSelectedDropFile(data); setSelectedFile(null); });
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => event.preventDefault();
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { showToastMessage("error", "Invalid file format"); return; }
    processFileForUpload(file, (data) => { setSelectedFile(data); setSelectedDropFile(null); });
  };
  const [isUploading, setIsUploading] = useState(false);

  const handleImport = async () => {
    if (!selectedFile && !selectedDropFile) return;
    if (isUploading) return;
    setIsUploading(true);
    const fileData = selectedFile?.file_data ?? selectedDropFile?.file_data;
    try {
      await uploadAPI(band?.value, fileData, currentTime, groundStationId);
      closeModal();
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const open_panel = (panel: string) => {
    setOpenPanel((prev) => {
      // In summary mode, keep the left panel permanently closed
      if (summaryMode && panel === "left") return { ...prev, left: false };
      return {
        left: panel === "left" ? !prev.left : prev.left || prev.right ? true : false,
        right: panel === "right" ? !prev.right : prev.right || prev.left ? true : false,
      };
    });
  };

  // Summary view: force console left panel closed
  useEffect(() => {
    if (!summaryMode) return;
    setOpenPanel((prev) => ({ ...prev, left: false, right: true }));
  }, [summaryMode]);



  /* ── Table filters ── */
  const [isTableFiltersVisible, setIsTableFiltersVisible] = useState(false);
  const [isResetActive, setIsResetActive] = useState(false);
  const [isSelectAllFiltersActive, setIsSelectAllFiltersActive] = useState(false);

  const [consoleTableColumns, setConsoleTableColumns] = useState([
    {
      label: "TcTmType",
      value: (entry) => entry?.CommandDetails?.tctmType,
      isSelected: true,
      width: "50",
      isSvg: true,
      svg: <TcTmCombinedIcon />,
      isFilterAvailable: false,
      renderIcon: (entry) => {
        const type = entry?.CommandDetails?.tctmType;
        switch (type) {
          case "Health Metric": return <HealthMetricSvg color={"#7AB3FF"} />;
          case "Beacon": return <BeaconSvg color={"#7AB3FF"} />;
          case "Invalid TM": return <InvalidTmSvg color={"#C91818"} />;
          case "TM": case "tm": return <TmIconGreen />;
          case "TC": case "tc": return <TcIconGreen />;
          case "TCTM": return <TcTmCombinedGreenIcon />;
          default: return null;
        }
      },
      dashboard: "summary" as const,
    },
    { label: "Time", value: (entry) => get_formated_time(entry?.CommandDetails?.timestamp), isSelected: getDefaultColumnSelection("Time", isValidateMode), width: "160", isFilterAvailable: true, dashboard: "summary" as const },
    { label: "TC/TM ID", value: (entry) => entry?.CommandDetails?.tctmId, isSelected: true, width: "100", isFilterAvailable: true, dashboard: "summary" as const },
    { label: "Receiver Time", value: (entry) => (entry?.CommandDetails?.["groundTimestamp"] ? get_formated_time(entry?.CommandDetails["groundTimestamp"] / 1000) : "--"), isSelected: true, width: "160", isFilterAvailable: true, dashboard: "operate" as const },
    { label: "Source", value: (entry) => entry?.CommandDetails?.saId, isSelected: true, width: "80", isFilterAvailable: true, dashboard: "summary" as const },
    { label: "Destination", value: (entry) => entry?.CommandDetails?.daId, isSelected: true, width: "100", isFilterAvailable: true, dashboard: "summary" as const },
    { label: "Associated Task", value: (entry) => entry?.TaskName, isSelected: getDefaultColumnSelection("Associated Task", isValidateMode), width: "140", isFilterAvailable: true, dashboard: "summary" as const },
    { label: "Bytes Length", value: (entry) => entry?.CommandDetails?.frameLength, isSelected: true, width: "100", isFilterAvailable: false, dashboard: "summary" as const },
    { label: "Type", value: (entry) => entry?.CommandDetails?.TmType ?? "--", isSelected: getDefaultColumnSelection("Type", isValidateMode), width: "100", isFilterAvailable: true, dashboard: "operate" as const },
    { label: "GS ID", value: (entry) => entry?.CommandDetails?.GroundStationId ?? "--", isSelected: getDefaultColumnSelection("GS ID", isValidateMode), width: "100", isFilterAvailable: true, dashboard: "operate" as const },
    { label: "Info", value: (entry) => entry?.Info ?? "--", isSelected: getDefaultColumnSelection("Info", isValidateMode), width: "140", isFilterAvailable: false, dashboard: "operate" as const },
    { label: "Results", value: (_entry) => undefined, isSelected: getDefaultColumnSelection("Results", isValidateMode), width: "150", isFilterAvailable: false, dashboard: "summary" as const },
  ]);

  const threeDotMenuPopUpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threeDotMenuPopUpRef.current && !threeDotMenuPopUpRef.current.contains(event.target)) {
        setIsTableFiltersVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modal]);

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      setConsoleTableColumns((prev) => arrayMove([...prev], oldIndex, newIndex));
    }
  };

  /* ── Selectable table filters ── */
  const [audioWaveformFilters, setAudioWaveformFilters] = useState({ S: false, UHF: false });
  const [tcTmTypeFilters, setTcTmTypeFilters] = useState({ invalid: false, healthMetric: false, beacon: false });
  const [allFilters, setAllFilters] = useState<AllFilterType>({ bandsFilter: [], tctmFilter: [], advanceFilters: [], timeFilter: { startTime: null, endTime: null } })
  // Builds the advance filter dropdown structure from API options
  const filterOption: FilterListType | null = useMemo(() => getFilterOption(consoleFilterOptions), [consoleFilterOptions])
  // Extracts available band options for the band filter selector
  const bandOptions: { label: string, value: string }[] = useMemo(() => getBands(consoleFilterOptions), [consoleFilterOptions])
  const { simulation_time } = useScenario()

  const reset_filters = () => {
    const currentSessionId = sessionStorage.getItem("sessionId");
    if (!isValidSessionId(currentSessionId)) {
      sessionStorage.setItem("sessionId", uuidv4());
    }
    setAudioWaveformFilters({ S: false, UHF: false });
    setTcTmTypeFilters({ invalid: false, healthMetric: false, beacon: false });
    setConsoleTableColumns((prev) => {
      const next = prev.map((item) => ({
        ...item,
        isSelected: getDefaultColumnSelection(item.label, isValidateMode),
      }));
      setIsTableWidth(
        next.reduce((sum, item) => (item.isSelected ? sum + Number(item.width) : sum), 0)
      );
      return next;
    });
    const cleared = { bandsFilter: [], tctmFilter: [], advanceFilters: [], timeFilter: { startTime: null, endTime: null } };
    setAllFilters(cleared);
    handleChangeFilter(cleared);
  };

  // Keep mode-specific columns in sync whenever validate mode changes or console reopens.
  useEffect(() => {
    if (modal.open) {
      const current = sessionStorage.getItem("sessionId");
      if (!isValidSessionId(current)) {
        sessionStorage.setItem("sessionId", uuidv4());
      }
    } else {
      sessionStorage.removeItem("sessionId");
    }
  }, [modal.open]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("sessionId");
    };
  }, []);

  useEffect(() => {
    setConsoleTableColumns((prev) => {
      const next = prev.map((item) =>
        item.isSelected === getDefaultColumnSelection(item.label, isValidateMode)
          ? item
          : { ...item, isSelected: getDefaultColumnSelection(item.label, isValidateMode) }
      );
      setIsTableWidth(
        next.reduce((sum, item) => (item.isSelected ? sum + Number(item.width) : sum), 0)
      );
      return next;
    });
  }, [isValidateMode, modal.open]);

  useEffect(() => {
    const isAnyAudioFilterOff = Object.values(audioWaveformFilters).some((v) => v);
    const isAnyTcTmFilterOff = Object.values(tcTmTypeFilters).some((v) => v);
    const isAnyColumnUnselected = consoleTableColumns.some((col, index) => {
      if (index === 0) return false;
      if (!operateMode && !isValidateMode && col.dashboard === "operate") return false;
      if (col.label === "Results" && !isValidateMode) return false;
      return col.isSelected === false;
    });
    setIsResetActive(
      isAnyAudioFilterOff ||
      isAnyTcTmFilterOff ||
      (allFilters?.bandsFilter?.length ?? 0) > 0 ||
      (allFilters?.tctmFilter?.length ?? 0) > 0 ||
      (allFilters?.advanceFilters?.length ?? 0) > 0 ||
      !!allFilters?.timeFilter?.startTime ||
      !!allFilters?.timeFilter?.endTime
    );
    setIsSelectAllFiltersActive(isAnyColumnUnselected);
  }, [audioWaveformFilters, tcTmTypeFilters, consoleTableColumns, allFilters, operateMode, isValidateMode]);

  // Ensure we attempt a safe cleanup on navigation (history back) and on unmount.
  // The cleanup is defensive and will no-op if required data is missing.
  useEffect(() => {
    const handlePopState = () => {
      // navigation via back/forward - attempt safe cleanup
      cleanupFilterGroupsApi();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Safe cleanup on component unmount
      cleanupFilterGroupsApi();
    };
  }, []);

  useEffect(() => {
    const close_open_modal = (event) => {
      if (commandCentreFreeze) return;
      if (event.target instanceof Element && (
        event.target.closest(".console__container") ||
        event.target.closest(".custom__menu__list") ||
        event.target.closest(".rp__stop-banner") ||
        event.target.closest(".upload-modal-overlay") ||
        event.target.closest(".upload-modal-container") ||
        event.target.closest(".custom__selector__select__container") ||
        event.target.closest(".custom__selector__option__container") ||
        event.target.closest(".confirmation__dialog__container") ||
        event.target.closest(".MuiPickersPopper-root") ||
        event.target.closest(".MuiPopover-root") ||
        event.target.closest(".MuiModal-root") ||
        event.target.closest(".MuiPaper-root")
      )) return;

      const currentModal = get_modal_details("console");
      // Close console if it's open and NOT pinned
      if (currentModal.open && !currentModal.pinned) {
        close_modal("console");
        clearTaskSessionStorage();
        sessionStorage.removeItem("taskType");
        sessionStorage.removeItem("sessionId");
        set_show_rawbytes(false);
        setCommandType({ value: "Bus", label: "Bus Command" })
      }
    };
    // Use capture phase to ensure this fires before other handlers
    document.addEventListener("click", close_open_modal, true);
    return () => document.removeEventListener("click", close_open_modal, true);
  }, [get_modal_details, close_modal]);

  const handleEpochChange = (date) => {
    if (new Date(date).getFullYear() <= EPOCH_MAX_YEAR) handleDateChange(date);
    else showToastMessage("error", "Please select a date before 2038");
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const time = Math.floor(date.$d.getTime() / 1000);
    setSelectedTime(date);
    setCurrentTime(time);
  };

  const handleChangeFilter = async (filters) => {
    window.dispatchEvent(new CustomEvent("postFilters", { detail: filters }));
    window.dispatchEvent(new CustomEvent("removeSelection"));
  };

  useEffect(() => {
    window.addEventListener("openConsole", reset_filters);
    return () => window.removeEventListener("openConsole", reset_filters);
  }, [reset_filters]);

  // ─── Communication tabs ───────────────────────────────────────────────────
  // TEST tab is only shown for validate deploymentType
  const commTabs: { type: CommType; label: string }[] = useMemo(() => {
    const base: { type: CommType; label: string }[] = [
      { type: "TT&C", label: "TT&C" },
      { type: "TASK", label: "TASK" },
    ];
    if (isValidateMode) {
      base.push({ type: "TEST", label: "TEST CASE" });
      base.push({ type: "TEST PLAN", label: "TEST PLAN" });
    }
    return base;
  }, [isValidateMode]);

  return (
    <Modal
      open={modal.open}
      pinned={modal.current_modal === "console" && modal.pinned}
      disabled={(modal.current_modal === "console" && modal.pinned) || resizing}
      class_name={`${modal.current_modal === "console" ? "current__modal" : ""} ${isConsoleMaximized ? "consoleMaximized" : ""}`}
    ><>
        <div className="console__container" onClick={handleOpenConsole}>
          {isModalOpen &&
            ReactDOM.createPortal(
              <div className="upload-modal-overlay" onClick={closeModal}>
                <div className="upload-modal-container" onClick={(e) => e.stopPropagation()}>
                  <div className="upload-modal-header">
                    <h2>Upload Raw Bytes</h2>
                    <button className="upload-modal-close" onClick={closeModal}> &times; </button>
                  </div>
                  <div className="d-flex justify-content-between align-items-start gap-3">

                    {/* Band Type */}
                    <div className="d-flex flex-column w-100">
                      <h6 className="m-0">Band Type</h6>
                      <CustomSelect options={ALL_BANDS} value={band} onChange={setBand} />
                    </div>

                    {/* Ground Station ID */}
                    <div className="d-flex flex-column w-100">
                      <h6 className="m-0">Ground Station ID</h6>
                      <Form.Control
                        className="input__preview__container_big bg-transparent"
                        type="text"
                        style={{ height: "30px" }}
                        placeholder="Enter GsId"
                        value={groundStationId}
                        onChange={(e) => setGroundStationId(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  <h6 className="pt-2 pb-0 mb-0">Epoch</h6>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={dayjs.utc(selectedTime)} // Set current UTC time as the default value
                      format="YYYY-MM-DD HH:mm:ss" // Set the desired format for display
                      onChange={handleEpochChange}
                      sx={DATE_PICKER_SX}
                    />
                  </LocalizationProvider>

                  <div
                    className="upload-modal-content"
                    style={{
                      pointerEvents: band?.value && groundStationId ? "auto" : "none",
                      opacity: band?.value && groundStationId ? 1 : 0.5,
                    }}
                  >
                    {/* Drag & Drop Zone */}
                    <div className="upload-drop-zone" onDrop={handleDrop} onDragOver={handleDragOver}>
                      {selectedDropFile ? selectedDropFile.name : "Drag & Drop file here"}
                    </div>

                    {/* Select from Device */}
                    <label className="upload-device-zone">
                      {selectedFile ? selectedFile.name : "Select from Device"}
                      <input type="file" className="d-none" onChange={handleFileSelect} />
                    </label>
                  </div>

                  <div className="upload-modal-footer">
                    <button className="upload-cancel-btn" onClick={closeModal} disabled={isUploading}> Cancel </button>
                    <button className="upload-import-btn" onClick={handleImport} disabled={(!selectedFile && !selectedDropFile) || isUploading}> {isUploading ? "Importing..." : "Import"} </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          <div
            className={`console__container__left__panel
          ${(scenario_completed || multiplier !== 1) ? "disabled" : ""}
          ${(openPanel.left && !scenario_completed && multiplier === 1) ? "active" : ""}`}
          >
            <div className="console__container__left__panel__container">
              <div className="left__panel__top__container">
                <div className="left__panel__header">
                  <span className="header__title">Console</span>
                  <div className="d-flex align-items-center gap-1">
                    <button
                      className="left__panel__colapse__btn"
                      disabled={scenario_completed || multiplier !== 1}
                      onClick={handleOpenLeftPanel}
                    >
                      <CollapseSvg />
                    </button>
                    <button
                      className={`left__panel__pin__btn ${modal.pinned ? "pinned" : ""} ${openPanel.right ? "d-none" : ""}`}
                      onClick={handlePinFunctionality}
                    >
                      <PinSvg />
                    </button>
                    <button
                      className={`left__panel__close__btn ${!openPanel.right ? "d-flex" : "d-none"}`}
                      onClick={handleCloseConsole}
                    >
                      <i className="fe fe-x fs-16" />
                    </button>
                  </div>
                </div>
                {(dashboard === 'simulation' && multiplier !== 1) &&
                  <div className="position-relative">
                    <div className='lock__icon__console__box'
                      onMouseEnter={() => setIsLockHovered(true)}
                      onMouseLeave={() => setIsLockHovered(false)}
                    >
                      <ConsoleLockIcon />
                    </div>
                  </div>
                }
                <div
                  className="left__panel__body"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="left__panel__body__communication__btn__container">
                    {commTabs.map(({ type, label }) => (
                      <button
                        className={`communication_btn ${communicationType === type ? "active" : ""} ${type === "TEST" && communicationType === type ? "communication_btn--test" : ""
                          }`}
                        key={type}
                        onClick={() => handleCommunicationBtnSelection(type)}
                        disabled={type === 'TASK' && !operateMode}
                        data-testid={`console-comm-tab-${type.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {communicationType === "TT&C" && (
                    <TTCFormSection
                      communication_type={communicationType}
                      command_type={commandType}
                      set_command_type={setCommandType}
                      busComponentOptions={busComponentOptions}
                      fetchBusCommands={fetchBusCommands}
                      busCommandOptions={busCommandOptions}
                      allPayloadComponents={allPayloadComponents}
                      formContainerHeight={formContainerHeight}
                    />
                  )}
                  {communicationType === "TASK" && <TaskForm onFormSubmit={handleFormSubmit} />}
                  {(communicationType === "TEST" || communicationType === "TEST PLAN") && (
                    <TestFormSection
                      key={communicationType}
                      mode={communicationType}
                      formContainerHeight={formContainerHeight}
                      onFormChange={handleTestFormChange}
                    />
                  )}
                </div>
              </div>
              {openPanel.left && !summaryMode && <div className="left__panel_bottom__container">
                <FormSubmit
                  isFormValid={isFormValid}
                  formData={formData}
                  postData={postData}
                  communicationType={communicationType}
                  setFormContainerHeight={setFormContainerHeight}
                  isValidationRunning={isValidationRunning}
                />
              </div>}
            </div>
          </div>
          <div className={`console__container__right__panel ${openPanel.right ? "active" : ""}`}>
            <div className="console__container__right__panel__container">
              <div className="right__panel__header">
                <span className="header__title">Messages</span>
                <div className="d-flex align-items-center gap-2">
                  <div className="console__header__raw__bytes__div">
                    <button
                      className={`custom__toggle__btn__container ${show_rawbytes ? "active" : ""}`}
                      onClick={() => {
                        set_show_rawbytes(!show_rawbytes)
                        setIsFrozen(false)
                      }}
                      data-testid="console-raw-bytes-toggle-btn"
                    />
                    <span>Raw Bytes</span>
                    {isValidateMode && (
                      <CustomToolTip title="Queue" placement="bottom">
                        <div
                          className="raw__bytes__icon__container"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsQueuePanelOpen((prev) => !prev);
                          }}
                          style={{ cursor: "pointer" }}
                          title="Queue"
                          data-testid="console-queue-toggle-btn"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="9" viewBox="0 0 11 9" fill="none">
                            <path d="M2.70922 0.5H10.5M2.70922 4.5H10.5M2.70922 8.5H10.5M0.507791 0.5H0.515582M0.5 4.5H0.507791M0.507791 8.5H0.515582" stroke="#F0F0F0" strokeOpacity="0.8" style={{ stroke: "#F0F0F0", strokeOpacity: 0.8 }} strokeLinecap="round" />
                          </svg>
                        </div>
                      </CustomToolTip>
                    )}
                  </div>
                  {/* this will need in future */}
                  {/* <button
                  onClick={openModal}
                  className={`right__panel__colapse__btn ${isConsoleMaximized ? "collapse" : ""}`}
                  onClick={() => setIsConsoleMaximized(!isConsoleMaximized)}
                  style={{ backgroundColor: "transparent" }}
                >
                  <ExpandConsoleViewIcon />
                </button> */}
                  <button
                    className={`right__panel__pin__btn ${modal.pinned ? "pinned" : ""} ${!openPanel.right ? "d-none" : ""}`}
                    onClick={handlePinFunctionality}
                  >
                    <PinSvg />
                  </button>
                  <button
                    className={`right__panel__close__btn ${openPanel.right ? "d-flex" : "d-none"}`}
                    onClick={handleCloseConsole}
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>

              <FilterGroupsHeader setAllFilters={setAllFilters} allFilters={allFilters} handleChangeFilter={handleChangeFilter} />

              {/* Second section of the header for the filters */}
              {!show_rawbytes && (
                <div className="right__panel__header__filters" style={{ padding: "12px 0px", justifyContent: "flex-end" }}>
                  <CustomFilter
                    timeRange={simulation_time ? {
                      start: (operateMode ? scenario?.scenarioStartTime : simulation_time?.start_time) as number,
                      end: (operateMode ? undefined : simulation_time?.end_time) as number,
                    } : undefined}
                    alignment='left'
                    bandsFilter={{
                      bands: bandOptions,
                      onClick: (bandKey) =>
                        setAudioWaveformFilters((prev) => ({
                          ...prev,
                          [bandKey]: !prev[bandKey]
                        })),
                      applyFilter: (filters: { [key: string]: any }) => {
                        handleChangeFilter(filters)
                      }
                    }}
                    tctmFilter={{
                      tctms: [{ label: 'Health Metric', value: 'healthMetric', Icon: <HealthMetricSvg color={undefined} /> }, { label: 'Beacon', value: 'beacon', Icon: <BeaconSvg color={undefined} /> }, { label: 'Invalid TM', value: 'invalid', Icon: <InvalidTmSvg color={undefined} /> }],
                      onClick: (tctm: any) => {
                        const key = tctm?.value;
                        if (!key) return;
                        setTcTmTypeFilters((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
                      },
                      applyFilter: (filters: { [key: string]: any }) => {
                        handleChangeFilter(filters)
                      }
                    }}
                    timeFilter={{
                      applyFilter: (filters: { [key: string]: any }) => {
                        handleChangeFilter(filters)
                      }
                    }}
                    advanceFilter={{
                      filterIcon: null,
                      filterList: filterOption,
                      applyFilter: (filters: { [key: string]: any }) => {
                        handleChangeFilter(filters)
                      }
                    }}
                    allFilters={allFilters}
                    setAllFilters={setAllFilters}
                    filterCase={'console'}
                    boundingScreen={'console__container__right__panel'}
                  />
                  <div className="right__panel__header__filters__input__div">
                    <button className={`right__panel__header__filters__reset__btn ${isResetActive ? "active" : "inactive"}`} onClick={reset_filters}>
                      Clear
                    </button>
                    <button className="right__panel__header__filters__icons" onClick={exportMessages}>
                      <CustomToolTip title={"Download File"} placement="top">
                        <DownloadIcon />
                      </CustomToolTip>
                    </button>
                    <button className={`right__panel__header__filters__icons bg-transparent ${!operateMode ? 'd-none' : ''}`} onClick={openModal}>
                      <CustomToolTip title={"Upload File"} placement="top">
                        <UploadIcon />
                      </CustomToolTip>
                    </button>

                    <button className="right__panel__header__filters__icons" onClick={() => setIsTableFiltersVisible(!isTableFiltersVisible)}>
                      <CustomToolTip title={"Select Columns"} placement="top">
                        <ThreeDotIcon />
                      </CustomToolTip>
                    </button>

                    {isTableFiltersVisible && (() => {
                      const AnyMenu = ThreeDotMenuPopUp as any;
                      return (
                        <AnyMenu
                          useDragHandle
                          onSortEnd={handleSortEnd}
                          helperClass="draggable-item-console-helper"
                          lockOffset="0%"
                          lockAxis="y"
                          axis="y"
                          lockToContainerEdges={true}
                          threeDotMenuPopUpRef={threeDotMenuPopUpRef}
                          isSelectAllFiltersActive={isSelectAllFiltersActive}
                          setIsTableWidth={setIsTableWidth}
                          consoleTableColumns={consoleTableColumns}
                          setConsoleTableColumns={setConsoleTableColumns}
                          operateMode={operateMode}
                          isTableWidth={isTableWidth}
                          dashboard={dashboard}
                          isValidateMode={isValidateMode}
                        />
                      );
                    })()}
                  </div>
                </div>
              )}

              {show_rawbytes && (
                <div className="right__panel__header__filters" style={{ padding: "12px 0px", justifyContent: "flex-end" }}>
                  <div className="right__panel__header__filters__input__div">
                    <button className="right__panel__header__filters__icons d-none">
                      <CustomToolTip title={"Download File"} placement="top">
                        <DownloadIcon />
                      </CustomToolTip>
                    </button>
                    <button className={`right__panel__header__filters__icons bg-transparent ${!operateMode ? 'd-none' : ''}`} onClick={openModal}>
                      <CustomToolTip title={"Upload File"} placement="top">
                        <UploadIcon />
                      </CustomToolTip>
                    </button>
                  </div>
                </div>
              )}

              <div className="right__panel__body">
                {/* <div className='right__panel__body__header'>
                <button className={`header__badge__btn active`}>IQ Spacecom Xlink-S</button>
                <button className={`header__badge__btn`}>IQ Spacecom Xlink-X</button>
                <button className={`header__badge__btn`}>OpenLST-A UHF</button>
              </div> */}
                {!show_rawbytes && (
                  <ConsoleOutputSection
                    error={error}
                    commandDetails={commandDetails}
                    getCommandDetails={getCommandDetails}
                    set_resizing={setResizing}
                    consoleTableColumns={consoleTableColumns}
                    isTableWidth={isTableWidth}
                    allFilters={allFilters}
                    isFrozen={isFrozen}
                    setIsFrozen={setIsFrozen}
                  />
                )}
                {show_rawbytes && (
                  <RawOutputSection loading={loading} error={error} commandDetails={commandDetails} getCommandDetails={getCommandDetails} set_resizing={setResizing} />
                )}
              </div>
            </div>
          </div>
          {isValidateMode && (
            <QueuePanel
              isOpen={isQueuePanelOpen}
              onClose={() => setIsQueuePanelOpen(false)}
              activeTest={activeTest}
              executedTests={executedTests}
              isAborting={isValidationAborting}
              onAbort={abortActiveExecution}
            />
          )}
        </div>
        {/* {isConsoleMaximized && <Header />} */}
        {(dashboard === 'simulation' && multiplier !== 1) && (
          <div
            className="lock__console__hover__message"
            style={{ display: isLockHovered ? 'flex' : 'none' }}
          >
            <span>
              Executing Commands is disabled for <span style={{ color: '#CCF54E' }}>Accelerated Simulations</span>
            </span>
          </div>
        )}
      </></Modal>
  );
};

export default Console;
